import type { UserScoreSnapshot } from "./types";

const eventName = "cogito:score-changed";
const channelName = "cogito-user-score";

function isScoreSnapshot(value: unknown): value is UserScoreSnapshot {
  if (!value || typeof value !== "object") return false;
  const score = value as Partial<UserScoreSnapshot>;
  return Number.isInteger(score.score) && Number.isInteger(score.all_time_score);
}

export function publishScoreChange(score: UserScoreSnapshot) {
  window.dispatchEvent(new CustomEvent<UserScoreSnapshot>(eventName, { detail: score }));
  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel(channelName);
    channel.postMessage(score);
    channel.close();
  }
}

export function subscribeScoreChanges(listener: (score: UserScoreSnapshot) => void) {
  const emit = (value: unknown) => {
    if (isScoreSnapshot(value)) listener(value);
  };
  const onLocal = (event: Event) => emit((event as CustomEvent<UserScoreSnapshot>).detail);
  window.addEventListener(eventName, onLocal);
  const channel = "BroadcastChannel" in window ? new BroadcastChannel(channelName) : null;
  if (channel) channel.onmessage = (event: MessageEvent<unknown>) => emit(event.data);
  return () => {
    window.removeEventListener(eventName, onLocal);
    channel?.close();
  };
}

export function subscribeScoreBroadcasts(listener: (score: UserScoreSnapshot) => void) {
  if (!("BroadcastChannel" in window)) return () => undefined;
  const channel = new BroadcastChannel(channelName);
  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (isScoreSnapshot(event.data)) listener(event.data);
  };
  return () => channel.close();
}

export function subscribeScoreInvalidations(listener: () => void) {
  const unsubscribe = subscribeScoreChanges(listener);
  const onVisible = () => {
    if (document.visibilityState !== "hidden") listener();
  };
  window.addEventListener("focus", onVisible);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    unsubscribe();
    window.removeEventListener("focus", onVisible);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
